import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getCombinedList, queryCombinedList, resolveCombinedList } from '../services/combinedLists';
import { matchKey } from '../utils/combinedLists';
import { getSessionKey, useOnSessionChange } from '../utils/session';

/**
 * THE COMBINED LIST's data (KPR-1022; keepr-api docs/SCHEMA.md § Combined
 * lists): a saved list by id, or an unsaved definition; its sources and the
 * lined-up vocabulary (`resolve`); its rows a page at a time (`query`, a
 * keyset cursor), under the page's filter and sort.
 *
 *   useCombinedList({ listId, definition, q, sort }) → {
 *     query, sort     what is read: `q` / `sort` as given, or — when null —
 *                     the saved list's stored ones (an unsaved list: none)
 *     list            the saved list (null for an unsaved one)
 *     definition      { match, exclude, include } in force
 *     notFound        the list is not this person's, or the address names
 *                     no definition (one state: the API answers one 404)
 *     vocabulary      { elements } — each entry's id is its token
 *     sources         the first page's sources (counts, notes) once read,
 *                     else resolve's (no counts yet)
 *     refusal         { code, message, ... } when the API said no to the
 *                     definition itself (too many sources, a bad match)
 *     items, cursor, unknown, loading, loadingMore, error,
 *     hasMore, loadMore(), reload(), setList(list),
 *     refresh(), refreshing, refreshError   the person's Refresh (KPR-1346)
 *   }
 *
 * A saved list is always read through `query` with its stored definition: the
 * filter and sort on screen are the page's (they start as the stored ones),
 * so "Save changes" can tell them apart. A cursor refused after the server's
 * secret rotated (`invalid_cursor`) starts the list again from its first page.
 * An answer for an earlier question, or for another account, is dropped.
 */

const sortKey = sort => (sort && sort.field ? `${sort.field}:${sort.direction || 'desc'}` : '');

const codeOf = error => error?.response?.data?.code || error?.response?.data?.error || null;

const DEFAULT_SORT = Object.freeze({ field: 'primaryDate', direction: 'desc' });

export default function useCombinedList({ listId = null, definition = null, q: givenQ = null, sort: givenSort = null } = {}) {
	const [list, setList] = useState(null);
	const [listState, setListState] = useState({ loading: Boolean(listId), notFound: false, error: null });
	const [resolved, setResolved] = useState({ key: '', sources: [], vocabulary: { elements: [] }, loading: false, refusal: null, error: null });
	const [page, setPage] = useState({ key: '', items: [], cursor: null, sources: null, unknown: [], loading: false, loadingMore: false, error: null });
	const [attempt, setAttempt] = useState(0);
	const requestRef = useRef(0);
	const resolveRef = useRef(0);

	useOnSessionChange(() => {
		requestRef.current += 1;
		resolveRef.current += 1;
		setList(null);
		setPage(prev => ({ ...prev, items: [], cursor: null, sources: null }));
	});

	// A saved list: read it (its tokens written again if the secret rotated).
	useEffect(() => {
		if (!listId) { setList(null); setListState({ loading: false, notFound: false, error: null }); return undefined; }
		let live = true;
		const owner = getSessionKey();
		setListState({ loading: true, notFound: false, error: null });
		getCombinedList(listId)
			.then((data) => {
				if (!live || getSessionKey() !== owner) return;
				setList(data);
				setListState({ loading: false, notFound: !data, error: null });
			})
			.catch((error) => {
				if (!live || getSessionKey() !== owner) return;
				const status = error?.response?.status;
				setListState({ loading: false, notFound: status === 404 || status === 400, error: status === 404 || status === 400 ? null : error });
			});
		return () => { live = false; };
	}, [listId, attempt]);

	const def = useMemo(() => {
		if (listId) return list ? { match: list.match, exclude: list.exclude || [], include: list.include || [] } : null;
		return definition;
	}, [listId, list, definition]);
	// The filter and sort read: the page's, else the saved list's own.
	const q = givenQ !== null ? givenQ : (list?.q || '');
	const sort = givenSort || list?.sort || DEFAULT_SORT;

	const defKey = useMemo(() => (def ? JSON.stringify([matchKey(def.match), def.exclude || [], def.include || []]) : ''), [def]);

	// The sources and the vocabulary: once per definition.
	useEffect(() => {
		if (!def) return;
		const token = ++resolveRef.current;
		const owner = getSessionKey();
		setResolved(prev => ({ ...prev, key: defKey, loading: true, refusal: null, error: null }));
		resolveCombinedList(def)
			.then((data) => {
				if (token !== resolveRef.current || getSessionKey() !== owner) return;
				setResolved({ key: defKey, sources: data.sources, vocabulary: data.vocabulary, loading: false, refusal: null, error: null });
			})
			.catch((error) => {
				if (token !== resolveRef.current || getSessionKey() !== owner) return;
				const status = error?.response?.status;
				const refusal = status === 400 ? { code: codeOf(error), ...(error.response.data || {}) } : null;
				setResolved({ key: defKey, sources: [], vocabulary: { elements: [] }, loading: false, refusal, error: refusal ? null : error });
			});
		// `def` is read through `defKey`: the same definition is the same read.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defKey, attempt]);

	const queryKey = `${defKey}|${q || ''}|${sortKey(sort)}`;

	const run = useCallback((cursor) => {
		if (!def) return;
		const token = ++requestRef.current;
		const owner = getSessionKey();
		setPage(prev => (cursor
			? { ...prev, loadingMore: true, error: null }
			: { key: queryKey, items: [], cursor: null, sources: null, unknown: [], loading: true, loadingMore: false, error: null }));
		queryCombinedList(def, { q, sort, cursor })
			.then((data) => {
				if (token !== requestRef.current || getSessionKey() !== owner) return;
				setPage(prev => ({
					key: queryKey,
					items: cursor ? [...prev.items, ...data.items] : data.items,
					cursor: data.cursor,
					// Counts come with the first page only.
					sources: cursor ? prev.sources : data.sources,
					unknown: data.unknown,
					loading: false,
					loadingMore: false,
					error: null,
				}));
			})
			.catch((error) => {
				if (token !== requestRef.current || getSessionKey() !== owner) return;
				if (cursor && codeOf(error) === 'invalid_cursor') { run(null); return; }
				setPage(prev => ({ ...prev, loading: false, loadingMore: false, error }));
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey]);

	useEffect(() => {
		if (!def || resolved.refusal) return;
		run(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey, attempt, Boolean(resolved.refusal)]);

	// The person's Refresh in flight (below): Show more waits for it (KPR-1346).
	const refreshRef = useRef(null);
	const loadMore = useCallback(() => {
		if (refreshRef.current) return;
		if (page.cursor && !page.loading && !page.loadingMore) run(page.cursor);
	}, [page.cursor, page.loading, page.loadingMore, run]);

	const reload = useCallback(() => setAttempt(n => n + 1), []);

	// The person's Refresh (KPR-1346): the same question asked again from the
	// first page, following the cursor until it holds as many rows as are on
	// screen (or the list ends), so the person keeps their place. The rows,
	// counts and cursor on screen stay until it lands, then are replaced in
	// one go; a failure changes nothing and says so in `refreshError`. One at
	// a time: a press while one runs gets the same promise. Never rejects.
	const [refreshState, setRefreshState] = useState({ key: '', refreshing: false, error: null });
	const pageRef = useRef(page);
	pageRef.current = page;
	const refresh = useCallback(() => {
		if (refreshRef.current) return refreshRef.current;
		const current = pageRef.current;
		if (!def || current.loading || current.key !== queryKey) return Promise.resolve(false);
		const token = ++requestRef.current;
		const owner = getSessionKey();
		const want = Math.max(1, current.items.length);
		setRefreshState({ key: queryKey, refreshing: true, error: null });
		const live = () => token === requestRef.current && getSessionKey() === owner;
		const readFrom = async (cursor, rows, first) => {
			const data = await queryCombinedList(def, { q, sort, cursor });
			if (!live()) return null;
			const all = [...rows, ...(data.items || [])];
			const head = first || data;
			if (!data.cursor || all.length >= want) return { items: all, cursor: data.cursor, sources: head.sources, unknown: head.unknown };
			return readFrom(data.cursor, all, head);
		};
		const run = readFrom(null, [], null).then((read) => {
			if (!read || !live()) return false;
			setPage({ key: queryKey, items: read.items, cursor: read.cursor, sources: read.sources, unknown: read.unknown, loading: false, loadingMore: false, error: null });
			setRefreshState({ key: queryKey, refreshing: false, error: null });
			return true;
		}).catch((error) => {
			if (!live()) return false;
			// The page in flight was this refresh's; the rows stay as they were.
			setPage(prev => ({ ...prev, loadingMore: false }));
			setRefreshState({ key: queryKey, refreshing: false, error });
			return false;
		}).finally(() => {
			if (refreshRef.current !== run) return;
			refreshRef.current = null;
			// Overtaken (a Retry, another account): it is no longer running.
			setRefreshState(prev => (prev.refreshing ? { ...prev, refreshing: false } : prev));
		});
		refreshRef.current = run;
		return run;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey]);
	// A new question ends a refresh of the old one.
	useEffect(() => {
		refreshRef.current = null;
		setRefreshState(prev => (prev.refreshing || prev.error ? { key: '', refreshing: false, error: null } : prev));
	}, [queryKey, attempt]);
	const refreshing = refreshState.refreshing && refreshState.key === queryKey;
	const refreshError = refreshState.error && refreshState.key === queryKey ? refreshState.error : null;

	const replaceItem = useCallback((item) => {
		if (!item?._id) return;
		setPage(prev => ({ ...prev, items: prev.items.map(row => (row._id === item._id ? { ...row, ...item } : row)) }));
	}, []);

	const sources = page.sources && page.key === queryKey ? page.sources : resolved.sources;
	const firstLoad = (listId && listState.loading) || (def && (resolved.loading || resolved.key !== defKey));

	return {
		list,
		setList,
		definition: def,
		query: q,
		sort,
		notFound: listId ? listState.notFound : !definition,
		listError: listState.error,
		vocabulary: resolved.vocabulary,
		resolving: Boolean(firstLoad),
		sources,
		refusal: resolved.refusal,
		resolveError: resolved.error,
		items: page.items,
		cursor: page.cursor,
		unknown: page.unknown,
		loading: Boolean(firstLoad) || page.loading || page.key !== queryKey,
		loadingMore: page.loadingMore,
		error: page.error || resolved.error || listState.error,
		hasMore: Boolean(page.cursor),
		loadMore,
		reload,
		refresh,
		refreshing,
		refreshError,
		replaceItem,
	};
}
